/**
 * Occurrence-preserving overlap review between a current CSV export and an
 * optional prior export from the same account/format.
 *
 * Equality is a review hint built from {@link prepareCsvRows} — the same
 * date/amount/sign/currency/description values {@link emitBeancount} would
 * write. Matching never excludes a row; the reader does that explicitly.
 */

import { CSV_PROFILE_FIELDS } from "@/lib/csv-mapping-profiles";
import {
  type ColumnMapping,
  type ConvertOptions,
  type PreparedCsvRow,
  type PrepareCsvRowResult,
  parseCsv,
  prepareCsvRows,
  preparedAmountText,
  preparedRowMatchKey,
  type ParsedCsv,
} from "@/lib/csv-to-beancount";

export type OverlapStatusReason =
  | "empty_prior"
  | "incompatible_headers"
  | "incompatible_shape"
  | "missing_required_columns"
  | "blocking_config";

export interface OverlapProposal {
  /** 0-based index into the current data rows. */
  currentRowIndex: number;
  /** 1-based source line of the current row. */
  currentLine: number;
  /** 0-based index into the prior data rows. */
  priorRowIndex: number;
  /** 1-based source line of the prior row. */
  priorLine: number;
  date: string;
  amountText: string;
  currency: string;
  description: string;
}

export interface OverlapRowIssue {
  rowIndex: number;
  line: number;
  reason: string;
  side: "current" | "prior";
}

export interface OverlapComparison {
  status: "ready" | "incompatible" | "empty";
  /** Present when status is not `ready`. UI translates this code. */
  statusReason?: OverlapStatusReason;
  proposals: OverlapProposal[];
  /** Current rows that prepared cleanly but found no prior occurrence. */
  unmatchedCurrent: PreparedCsvRow[];
  /** Prior rows that prepared cleanly but were not consumed by a proposal. */
  unmatchedPrior: PreparedCsvRow[];
  /** Rows that could not be prepared — never classified as overlap. */
  issues: OverlapRowIssue[];
  /** Mapping applied to the prior file when status is `ready`. */
  priorMapping: ColumnMapping | null;
}

function emptyComparison(
  status: OverlapComparison["status"],
  statusReason?: OverlapStatusReason,
): OverlapComparison {
  return {
    status,
    statusReason,
    proposals: [],
    unmatchedCurrent: [],
    unmatchedPrior: [],
    issues: [],
    priorMapping: null,
  };
}

/**
 * Resolve the current mapping onto a prior file by unambiguous header names.
 * Without headers on both sides, the same column indexes are reused only when
 * the prior width covers every mapped index — otherwise we refuse to guess.
 */
export function resolvePriorMapping(
  currentHeaders: readonly string[] | null,
  priorHeaders: readonly string[] | null,
  mapping: ColumnMapping,
  priorWidth: number,
):
  | { ok: true; mapping: ColumnMapping }
  | { ok: false; reason: OverlapStatusReason } {
  const mappedIndexes = CSV_PROFILE_FIELDS.map((field) => mapping[field] ?? null).filter(
    (index): index is number => index !== null,
  );
  if (mappedIndexes.length === 0) {
    return { ok: false, reason: "missing_required_columns" };
  }
  if (mapping.date === null) {
    return { ok: false, reason: "missing_required_columns" };
  }
  if (
    mapping.amount === null &&
    mapping.debit === null &&
    mapping.credit === null
  ) {
    return { ok: false, reason: "missing_required_columns" };
  }

  if (currentHeaders !== null && priorHeaders !== null) {
    const priorCounts = new Map<string, number>();
    for (const raw of priorHeaders) {
      const name = raw.trim();
      if (name === "") continue;
      priorCounts.set(name, (priorCounts.get(name) ?? 0) + 1);
    }
    const priorFirst = new Map<string, number>();
    priorHeaders.forEach((raw, index) => {
      const name = raw.trim();
      if (name === "" || priorFirst.has(name)) return;
      priorFirst.set(name, index);
    });

    const resolved: ColumnMapping = {
      date: null,
      description: null,
      amount: null,
      debit: null,
      credit: null,
      currency: null,
      category: null,
    };

    for (const field of CSV_PROFILE_FIELDS) {
      const index = mapping[field] ?? null;
      if (index === null) {
        resolved[field] = null;
        continue;
      }
      const name = currentHeaders[index]?.trim() ?? "";
      if (name === "") {
        return { ok: false, reason: "incompatible_headers" };
      }
      if ((priorCounts.get(name) ?? 0) !== 1) {
        return { ok: false, reason: "incompatible_headers" };
      }
      resolved[field] = priorFirst.get(name) ?? null;
    }
    return { ok: true, mapping: resolved };
  }

  if (currentHeaders === null && priorHeaders === null) {
    const maxIndex = Math.max(...mappedIndexes);
    if (priorWidth <= maxIndex) {
      return { ok: false, reason: "incompatible_shape" };
    }
    return {
      ok: true,
      mapping: {
        date: mapping.date,
        description: mapping.description,
        amount: mapping.amount,
        debit: mapping.debit,
        credit: mapping.credit,
        currency: mapping.currency,
        category: mapping.category ?? null,
      },
    };
  }

  return { ok: false, reason: "incompatible_headers" };
}

function issuesFromResults(
  results: PrepareCsvRowResult[],
  side: "current" | "prior",
): OverlapRowIssue[] {
  const issues: OverlapRowIssue[] = [];
  for (const result of results) {
    if (result.ok) continue;
    issues.push({
      rowIndex: result.rowIndex,
      line: result.line,
      reason: result.reason,
      side,
    });
  }
  return issues;
}

/**
 * Match prepared prior rows against prepared current rows as a multiset:
 * each prior occurrence can propose at most one current occurrence, walked
 * in file order. Identical current rows stay independently addressable.
 */
export function matchPreparedOccurrences(
  current: readonly PreparedCsvRow[],
  prior: readonly PreparedCsvRow[],
): {
  proposals: OverlapProposal[];
  unmatchedCurrent: PreparedCsvRow[];
  unmatchedPrior: PreparedCsvRow[];
} {
  const priorQueues = new Map<string, PreparedCsvRow[]>();
  for (const row of prior) {
    const key = preparedRowMatchKey(row);
    const queue = priorQueues.get(key);
    if (queue) queue.push(row);
    else priorQueues.set(key, [row]);
  }

  const proposals: OverlapProposal[] = [];
  const unmatchedCurrent: PreparedCsvRow[] = [];

  for (const row of current) {
    const key = preparedRowMatchKey(row);
    const queue = priorQueues.get(key);
    const matched = queue?.shift();
    if (!matched) {
      unmatchedCurrent.push(row);
      continue;
    }
    proposals.push({
      currentRowIndex: row.rowIndex,
      currentLine: row.line,
      priorRowIndex: matched.rowIndex,
      priorLine: matched.line,
      date: row.date,
      amountText: preparedAmountText(row),
      currency: row.currency,
      description: row.description,
    });
  }

  const unmatchedPrior: PreparedCsvRow[] = [];
  for (const queue of priorQueues.values()) {
    unmatchedPrior.push(...queue);
  }
  unmatchedPrior.sort((a, b) => a.rowIndex - b.rowIndex);

  return { proposals, unmatchedCurrent, unmatchedPrior };
}

interface CompareCsvOverlapInput {
  current: ParsedCsv;
  priorText: string;
  mapping: ColumnMapping;
  options?: ConvertOptions;
}

/**
 * Compare an optional prior CSV against the current parse using the reader's
 * live mapping and convert options. Returns an incompatible status rather
 * than guessing when headers/shape cannot carry the mapping.
 */
export function compareCsvOverlap(
  input: CompareCsvOverlapInput,
): OverlapComparison {
  const priorText = input.priorText.trim();
  if (priorText === "") {
    return emptyComparison("empty", "empty_prior");
  }

  const prior = parseCsv(input.priorText);
  if (prior.rows.length === 0) {
    return emptyComparison("empty", "empty_prior");
  }

  const resolved = resolvePriorMapping(
    input.current.headers,
    prior.headers,
    input.mapping,
    prior.width,
  );
  if (!resolved.ok) {
    return emptyComparison("incompatible", resolved.reason);
  }

  const currentPrepared = prepareCsvRows(
    input.current.rows,
    input.mapping,
    input.options,
    input.current.headers !== null,
  );
  if (currentPrepared.blockingErrors.length > 0) {
    return emptyComparison("incompatible", "blocking_config");
  }

  const priorPrepared = prepareCsvRows(
    prior.rows,
    resolved.mapping,
    input.options,
    prior.headers !== null,
  );
  if (priorPrepared.blockingErrors.length > 0) {
    return emptyComparison("incompatible", "blocking_config");
  }

  const currentOk = currentPrepared.results
    .filter((result): result is { ok: true; row: PreparedCsvRow } => result.ok)
    .map((result) => result.row);
  const priorOk = priorPrepared.results
    .filter((result): result is { ok: true; row: PreparedCsvRow } => result.ok)
    .map((result) => result.row);

  const matched = matchPreparedOccurrences(currentOk, priorOk);
  return {
    status: "ready",
    proposals: matched.proposals,
    unmatchedCurrent: matched.unmatchedCurrent,
    unmatchedPrior: matched.unmatchedPrior,
    issues: [
      ...issuesFromResults(currentPrepared.results, "current"),
      ...issuesFromResults(priorPrepared.results, "prior"),
    ],
    priorMapping: resolved.mapping,
  };
}
